import type { ViewStyle } from 'react-native';

import { scale } from '@components/frame';
import { DashboardScope, DashboardResultKind } from '@api/domain/types';
import type { DashboardResult, MachineDashboardResult, CustomerDashboardResult, PartDashboardResult, NcFileDashboardResult } from '@api/domain/types';

import { BAR_MIN_RATIO, PANEL_TEST_ID_BY_VARIANT, PANEL_TITLE_BY_SCOPE, ROW_HEIGHT, ROW_LAYOUT, SHOW_ALL_TEST_ID_BY_VARIANT } from '@v2/pages/dashboard/common/constants/rows';
import type { RankBadgeVariant, RowBorderRadiusConfig } from '@v2/pages/dashboard/common/types/rows';
import type { PageOneMaxes } from '@v2/pages/dashboard/common/types/list';
import type { DashboardEntityScreenTarget } from '@v2/pages/dashboard/common/types/navigation';
import { DASHBOARD_ENTITY_SCREEN_BY_SCOPE } from '@v2/pages/dashboard/common/constants/navigation';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { injectConditionalObject, isPhone } from '@helpers';

export const getPanelTitle = (scope: DashboardScope, variant: RankBadgeVariant): string => PANEL_TITLE_BY_SCOPE[scope][variant];

export const getPanelTestId = (variant: RankBadgeVariant): string => PANEL_TEST_ID_BY_VARIANT[variant];

export const getShowAllTestId = (variant: RankBadgeVariant): string => SHOW_ALL_TEST_ID_BY_VARIANT[variant];

export const getRowHeight = (dashboardScope: DashboardScope): number => ROW_HEIGHT[dashboardScope];

export const getRowContainerStyle = (scope: DashboardScope, borderRadiusConfig?: RowBorderRadiusConfig): ViewStyle => ({
  flexDirection: 'row',
  alignItems: 'center',
  height: scale(getRowHeight(scope)),
  paddingHorizontal: scale(ROW_LAYOUT.horizontalPadding),
  ...injectConditionalObject(!!borderRadiusConfig?.top, {
    borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
    borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
  }),
  ...injectConditionalObject(!!borderRadiusConfig?.bottom, {
    borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
    borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
  }),
});

export const rowHasImage = (scope: DashboardScope): boolean =>
  !isPhone && (scope === DashboardScope.Part || scope === DashboardScope.NcFile);

export const isMachineResult = (result: DashboardResult): result is MachineDashboardResult => result.$type === DashboardResultKind.Machine;

export const isCustomerResult = (result: DashboardResult): result is CustomerDashboardResult => result.$type === DashboardResultKind.Customer;

export const isPartResult = (result: DashboardResult): result is PartDashboardResult => result.$type === DashboardResultKind.Part;

export const isNcFileResult = (result: DashboardResult): result is NcFileDashboardResult => result.$type === DashboardResultKind.NcFile;

export const computeBarWidthRatio = (value: number, max: number | null | undefined): number => {
  if (!max || max <= 0) return BAR_MIN_RATIO;
  const ratio = Math.abs(value) / max;
  if (ratio > 1) return 1;
  if (ratio < BAR_MIN_RATIO) return BAR_MIN_RATIO;
  return ratio;
};

export const computePageOneMaxes = (items: ReadonlyArray<DashboardResult>): PageOneMaxes => {
  let positive: number | null = null;
  let negative: number | null = null;
  for (const item of items) {
    const v = item.totalTimeTotalInSeconds ?? 0;
    if (v > 0) positive = Math.max(positive ?? 0, v);
    if (v < 0) negative = Math.max(negative ?? 0, Math.abs(v));
  }
  return { positive, negative };
};

export const getDashboardResultName = (item: DashboardResult): string => {
  if (isMachineResult(item)) return item.shortName ?? item.fullName ?? '';
  if (isCustomerResult(item)) return item.customerName ?? '';
  if (isPartResult(item)) return item.partName ?? '';
  if (isNcFileResult(item)) return item.ncFileName ?? '';
  return '';
};

export const getDashboardResultId = (item: DashboardResult): string => {
  if (isMachineResult(item)) return item.machineId?.toString() ?? '';
  if (isCustomerResult(item)) return item.customerId?.toString() ?? '';
  if (isPartResult(item)) return item.partId?.toString() ?? '';
  if (isNcFileResult(item)) return item.ncFileId?.toString() ?? '';
  return '';
};

export const getDashboardEntityScreenTarget = (scope: DashboardScope, item: DashboardResult): DashboardEntityScreenTarget | null => {
  const id = getDashboardResultId(item);
  if (!id) return null;
  const name = getDashboardResultName(item);
  return DASHBOARD_ENTITY_SCREEN_BY_SCOPE[scope](id, name);
};
